import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, HttpError } from '@/api/client';
import type { ProfileView, TaxStatus, VehicleType } from '@/api/types';
import { BigCTA } from '@/components/BigCTA';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { VehiclePicker } from '@/components/VehiclePicker';
import { LocationFields } from '@/components/LocationFields';
import { ErrorView, formatError } from '@/components/ErrorView';
import { FullPageSpinner } from '@/components/Spinner';
import { canonicalCity, canonicalDistrict } from '@/data/geo';
import { closeMiniApp, requestPhoneContact, showAlert, showConfirm } from '@/sdk/telegram';

const TAX_LABEL: Record<TaxStatus, string> = {
  SELF_EMPLOYED: 'Самозанятый',
  INDIVIDUAL: 'Физлицо',
  UNSET: 'Не указано',
};

export function ProfilePage() {
  const nav = useNavigate();
  const [p, setP] = useState<ProfileView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);
  const [pullingPhone, setPullingPhone] = useState(false);

  const [phone, setPhone] = useState('');
  const [taxStatus, setTaxStatus] = useState<TaxStatus>('UNSET');
  const [vehicle, setVehicle] = useState<VehicleType>('UNSET');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');

  useEffect(() => {
    api.getProfile()
      .then((res) => { setP(res); seed(res); })
      .catch(setErr);
  }, []);

  const seed = (v: ProfileView) => {
    setPhone(v.phone ?? '');
    setTaxStatus(v.taxStatus);
    setVehicle(v.defaultVehicle);
    setCity(v.defaultLocation.city ?? '');
    setDistrict(v.defaultLocation.district ?? '');
  };

  if (deleted) {
    return (
      <div className="page">
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 48 }}>👋</div>
          <div style={{ marginTop: 8 }}>Аккаунт удалён.</div>
          <div className="page-subtitle" style={{ marginTop: 4 }}>
            Можете закрыть это окно.
          </div>
        </div>
      </div>
    );
  }

  if (err && !p) return <div className="page"><ErrorView error={err} /></div>;
  if (!p) return <FullPageSpinner />;

  const pullPhoneFromTelegram = async () => {
    setEditError(null);
    setPullingPhone(true);
    try {
      const contact = await requestPhoneContact();
      if (!contact) {
        setEditError('Твоя версия Telegram не умеет делиться номером — введи вручную.');
        return;
      }
      // The backend verifies the signature and saves the number, so the value it returns is
      // the source of truth — do not echo back what the client parsed.
      const updated = await api.setPhoneFromTelegram(contact);
      setP(updated); seed(updated);
    } catch (e) {
      // Only an HttpError came from our backend. Everything else is the SDK saying the user
      // dismissed the prompt or the platform refused — "нет связи с сервером" would be a lie.
      setEditError(
        e instanceof HttpError
          ? (e.code === 'validation_error'
            ? 'Telegram вернул не российский номер — введи вручную.'
            : formatError(e))
          : 'Номер не получен — Telegram не дал доступ. Введи вручную.',
      );
    } finally {
      setPullingPhone(false);
    }
  };

  const save = async () => {
    setEditError(null);
    // Blank and the '+7' placeholder both mean "no number entered" — sending '' gets a
    // raw English 400 from the backend's E.164 check.
    const trimmedPhone = phone.trim();
    const phoneEntered = trimmedPhone !== '' && trimmedPhone !== '+7';
    if (phoneEntered && !/^\+7\d{10}$/.test(trimmedPhone)) {
      setEditError('Телефон в формате +7XXXXXXXXXX');
      return;
    }
    setSaving(true);
    try {
      // '' is an explicit clear, `undefined` omits the field entirely (see UpdateProfileRequest).
      // Sending undefined for an emptied field is what used to make clearing impossible.
      const updated = await api.updateProfile({
        phone: phoneEntered ? trimmedPhone : '',
        taxStatus,
        defaultVehicle: vehicle === 'UNSET' ? undefined : vehicle,
        // Canonicalise against the directory so "спб"/"Санкт-петербург" land in the same
        // analytics bucket instead of splitting the sample three ways.
        defaultCity: canonicalCity(city),
        defaultDistrict: canonicalDistrict(city, district),
      });
      setP(updated); seed(updated); setEdit(false);
    } catch (e) {
      setEditError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!await showConfirm('Удалить аккаунт? Действие необратимо. Баланс должен быть равен 0.')) return;
    try {
      await api.deleteProfile();
      // Do NOT navigate back into the app: the backend creates a user on demand for any
      // authenticated request, so one more /api/dashboard call would silently resurrect the
      // account the user just deleted. Close the Mini App, or park on a terminal screen.
      setDeleted(true);
      closeMiniApp();
    } catch (e) {
      await showAlert(formatError(e));
    }
  };

  return (
    <div className="page">
      <h1 className="page-title">Профиль</h1>

      <TrustScoreBadge level={p.trustLevel} levelLabel={trustLabel(p.trustLevel)} score={p.trustScore} />

      {!edit ? (
        <div className="card" style={{ padding: 0 }}>
          <Row label="Telegram" value={p.username ? `@${p.username}` : `#${p.telegramId}`} />
          <Row label="Телефон" value={p.phone ?? 'не указан'} />
          <Row label="Налоговый статус" value={TAX_LABEL[p.taxStatus]} />
          <Row label="Транспорт" value={vehicleLabel(p.defaultVehicle)} />
          <Row label="Город" value={p.defaultLocation.city ?? '—'} last />
        </div>
      ) : (
        <div className="card">
          {editError && <div className="error-banner" style={{ marginBottom: 12 }}>{editError}</div>}

          <div className="field">
            <label className="field-label" htmlFor="pf-phone">Телефон</label>
            <input id="pf-phone" className="field-input" inputMode="tel" placeholder="+7XXXXXXXXXX"
              value={phone} onChange={(e) => setPhone(e.target.value)} />
            <button
              type="button"
              className="big-cta secondary"
              style={{ minHeight: 40, marginTop: 6, fontSize: 14 }}
              onClick={pullPhoneFromTelegram}
              disabled={pullingPhone || saving}
            >
              {pullingPhone ? 'Запрашиваем…' : '📱 Взять из Telegram'}
            </button>
            <div className="field-hint">
              Telegram подтвердит номер сам — на него уйдёт выплата по СБП.
            </div>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <div className="field-label">Налоговый статус</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['SELF_EMPLOYED', 'INDIVIDUAL', 'UNSET'] as TaxStatus[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`big-cta ${taxStatus === t ? '' : 'secondary'}`}
                  style={{ flex: 1, minHeight: 44, padding: 10, fontSize: 13 }}
                  onClick={() => setTaxStatus(t)}
                >
                  {TAX_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <div className="field-label">Транспорт по умолчанию</div>
            <VehiclePicker value={vehicle} onChange={setVehicle} />
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <LocationFields
              labels
              city={city}
              district={district}
              onCity={setCity}
              onDistrict={setDistrict}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="big-cta secondary" onClick={() => { setEdit(false); setEditError(null); seed(p); }} disabled={saving} style={{ flex: 1 }}>
              Отмена
            </button>
            <button className="big-cta" onClick={save} disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}

      {!edit && <BigCTA variant="secondary" onClick={() => setEdit(true)}>Редактировать</BigCTA>}
      <BigCTA variant="secondary" onClick={() => nav('/wallet')}>Кошелёк</BigCTA>
      {!edit && <BigCTA variant="destructive" onClick={onDelete}>Удалить аккаунт</BigCTA>}
    </div>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', gap: 8, padding: 14,
        borderBottom: last ? 'none' : '1px solid var(--bk-border)',
      }}
    >
      <span style={{ color: 'var(--bk-hint)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// Same wording the backend sends in DashboardView.trustLevelLabel — one state, one name.
// SHADOWBAN stays deliberately vague: naming the mechanism would tell the user to open a
// second account, which is exactly what the shadowban exists to prevent.
function trustLabel(l: ProfileView['trustLevel']): string {
  return { SHADOWBAN: 'Ограничен', NEWBIE: 'Под подозрением', TRUSTED: 'Надёжный', ELITE: 'Элита' }[l];
}
function vehicleLabel(v: VehicleType): string {
  return { PEDESTRIAN: 'Пешком', BICYCLE: 'Вело/самокат', CAR: 'Авто', UNSET: 'Не указан' }[v];
}
